/**
 * @function ToastContext
 * @description Context for displaying toast messages.
 * @returns {JSX.Element | null} - The ToastContext component.
 */
import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { Toast } from './Toast'


/**
 * @interface ToastConfig
 * @description Configuration for the toast message.
 * @property {JSX.Element} icon - The icon to display.
 * @property {string} title - The title of the toast.
 * @property {string} description - The description of the toast.
 * @property {boolean} canClose - Whether the toast can be closed.
 * @property {number} timeout - The timeout for the toast.
 */
export interface ToastConfig {
    icon: JSX.Element
    title: string
    description?: string
    canClose?: boolean
    timeout?: number
}

/**
 * @interface ToastContextType
 * @description Type for the toast context.
 * @property {function} open - The function to open the toast.
 * @property {function} close - The function to close the toast.
 */
export interface ToastContextType {
    open: (toastConfig: ToastConfig) => void
    close: () => void
}

export const ToastContext = createContext<ToastContextType>(undefined)

/**
 * @function ToastAPIContext
 * @description Context for displaying toast messages.
 * @returns {JSX.Element | null} - The ToastAPIContext component.
 */
export function ToastAPIContext({ children }: PropsWithChildren) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [config, setConfig] = useState<ToastConfig>()

    useEffect(() => {
        if (!isOpen) return

        setTimeout(() => {
            setIsOpen(false)
        }, 3000)
    }, [isOpen])

    const API = {
        async open(toastConfig: ToastConfig) {
            setConfig(toastConfig)
            setIsOpen(true)
        },
        async close() {
            setConfig(undefined)
            setIsOpen(false)
        },
    }

    return (
        <ToastContext.Provider value={API}>
            {children}
            <div
                aria-live="assertive"
                className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-20"
            >
                <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                    <Toast isOpen={isOpen} onClose={API.close} {...config} />
                </div>
            </div>
        </ToastContext.Provider>
    )
}
